<template>
  <div class="h-full flex flex-col min-h-0">
    <div
      class="flex flex-col bg-white dark_bg-gray-800 overflow-hidden flex-1 min-h-0"
    >
      <!-- Messages -->
      <div
        class="flex-1 min-h-0 overflow-y-auto p-3 sm_p-4 flex flex-col gap-3"
        ref="messagesContainer"
      >
        <!-- Empty state: loading first, then welcome + suggestions -->
        <div
          v-if="chat.messages.length === 0"
          class="flex flex-col items-center h-full min-h-64 sm_min-h-80 p-3 sm_p-4"
          :class="!yaml ? 'justify-center' : 'justify-start'"
        >
          <template v-if="!yaml">
            <p class="text-gray-500 dark_text-gray-400 italic">
              Loading course...
            </p>
          </template>
          <template v-else>
            <!-- Welcome message -->
            <div
              class="flex flex-col gap-4 items-center pt-4 p-2.5 max-w-96 mb-6"
            >
              <span
                class="px-2 inline-block w-12 dark:opacity-90 [&_svg]:block [&_svg]:w-full [&_svg]:h-auto"
                v-html="logoRoundVwAi"
                aria-hidden="true"
              ></span>
              <p
                class="font-semibold text-black dark_text-gray-100 text-center"
              >
                How can I help you today?
              </p>
              <p
                class="font-normal text-gray-700 dark_text-gray-300 text-center"
              >
                I can help improve your course content, answer questions, and
                generate suggestions.
              </p>
            </div>
            <!-- Suggestion boxes -->
            <div class="flex flex-col gap-2 w-full max-w-96">
              <button
                v-for="(suggestion, idx) in suggestionItems"
                :key="idx"
                type="button"
                class="flex items-center gap-3 px-2.5 py-4 w-full rounded border border-indigo-100 dark_border-gray-600 bg-[rgba(245,243,255,0.56)] dark_bg-gray-800/30 hover:bg-indigo-50/80 dark_hover:bg-gray-700/50 hover:border-indigo-200 dark_hover:border-gray-500 transition-colors cursor-pointer text-left focus-visible_ring-4"
                @click="applySuggestion(suggestion)"
              >
                <Icon
                  :icon="suggestion.icon"
                  class="shrink-0 text-2xl text-indigo-500 dark_text-indigo-400"
                  aria-hidden="true"
                />
                <div class="flex flex-col gap-1 min-w-0 flex-1">
                  <span class="font-semibold text-gray-800 dark_text-gray-200">
                    {{ suggestion.title }}
                  </span>
                  <span class="font-medium text-gray-600 dark_text-gray-400">
                    {{ suggestion.description }}
                  </span>
                </div>
              </button>
            </div>
          </template>
        </div>

        <div
          v-for="(message, msgIndex) in chat.messages"
          :key="message.id"
          class="flex"
          :class="
            message.role === 'user'
              ? 'self-end max-w-[80%]'
              : 'self-start w-full'
          "
        >
          <div
            class="relative"
            :class="
              message.role === 'user'
                ? 'bg-slate-100 p-3 px-6 rounded-md dark_bg-gray-700'
                : 'w-full'
            "
          >
            <div
              class="flex items-center gap-1 font-semibold mb-1 opacity-70 uppercase text-gray-700 dark_text-gray-300"
            >
              <!-- Add course and screen pills when user messages, render normally -->
              <div
                v-if="message.role === 'user'"
                class="font-semibold mb-1 opacity-70 uppercase text-gray-700 dark_text-gray-300 flex gap-3"
                :title="getMessageText(message)"
              ></div>
            </div>

            <!-- For user messages, render normally -->
            <div
              v-if="message.role === 'user'"
              class="prose dark_prose-invert leading-relaxed break-words"
              v-html="renderMarkdown(stripTags(getMessageText(message)))"
            ></div>

            <!-- For assistant messages, parse and render chunks -->
            <div
              v-else
              class="leading-relaxed break-words text-gray-900 dark_text-gray-100 w-full"
            >
              <template
                v-for="chunk in parseMessageContent(getMessageText(message))"
                :key="chunk.key"
              >
                <!-- Regular text content -->
                <div
                  v-if="chunk.type === 'text'"
                  class="prose dark_prose-invert"
                  v-html="
                    renderMarkdown(
                      isMessageStreaming(message)
                        ? trimPartialTag(chunk.content)
                        : chunk.content
                    )
                  "
                ></div>

                <!-- Edit block - show PatchPreview component -->
                <div
                  v-else-if="chunk.type === 'edit' && !isLiteMode"
                  class="my-4 w-full"
                >
                  <PatchPreview
                    v-if="chunk.complete"
                    :screenYaml="chunk.content"
                    :currentYaml="yaml || ''"
                    :index="chunk.index"
                    :state="patchStates?.[message.id + '_' + chunk.index]"
                    :chunkKey="message.id + '_' + chunk.index"
                    :order="msgIndex * 10000 + chunk.index"
                    @accept="
                      screenYaml =>
                        onAcceptPatch(message.id, chunk.index, screenYaml)
                    "
                    @reject="() => onRejectPatch(message.id, chunk.index)"
                    @revert="
                      screenId =>
                        onRevertPatch(message.id, chunk.index, screenId)
                    "
                    @un-reject="() => onUnrejectPatch(message.id, chunk.index)"
                    @view="onViewPatch"
                  />
                  <pre
                    v-else
                    class="whitespace-pre-wrap text-gray-500 dark_text-gray-400 italic"
                  >
Generating patch…
                  </pre>
                </div>

                <!-- Text edit block - show TextEditPreview component -->
                <div
                  v-else-if="chunk.type === 'text-edit' && !isLiteMode"
                  class="my-4 w-full"
                >
                  <TextEditPreview
                    v-if="chunk.complete"
                    :content="chunk.content"
                    :currentYaml="yaml || ''"
                    :index="chunk.index"
                    :state="patchStates?.[message.id + '_' + chunk.index]"
                    :chunkKey="message.id + '_' + chunk.index"
                    :order="msgIndex * 10000 + chunk.index"
                    @accept="
                      screenYaml =>
                        onAcceptPatch(message.id, chunk.index, screenYaml)
                    "
                    @reject="() => onRejectPatch(message.id, chunk.index)"
                    @revert="
                      screenId =>
                        onRevertPatch(message.id, chunk.index, screenId)
                    "
                    @un-reject="() => onUnrejectPatch(message.id, chunk.index)"
                    @view="onViewPatch"
                  />
                  <pre
                    v-else
                    class="whitespace-pre-wrap text-gray-500 dark_text-gray-400 italic"
                  >
Generating text edit…
                  </pre>
                </div>
              </template>

              <LitePatchReviewBar
                v-if="
                  isLiteMode &&
                  !isMessageStreaming(message) &&
                  getLiteReviewForMessage(message).allGroups.length > 0
                "
                class="mt-4"
                :status="getLiteReviewStatusForMessage(message)"
                :index="getLiteNavigatorIndex(message)"
                :messageId="message.id"
                :total="getLiteReviewForMessage(message).allGroups.length"
                :has-newer-messages="hasNewerNonRejectedEdits(msgIndex)"
                :disable-actions="
                  isStreaming ||
                  getLiteReviewForMessage(message).pendingEdits.length === 0
                "
                :can-view="!!getCurrentLiteSelection(message)"
                :is-valid="getCurrentLiteSelection(message)?.isValid ?? false"
                :is-final="getCurrentLiteSelection(message)?.isFinal ?? true"
                :current-decision="getCurrentLiteSelectionDecision(message)"
                :show-previous-version="getShowPreviousVersion()"
                @update:show-previous-version="setShowPreviousVersion"
                @view-current="
                  emitLiteSelection(message, { navigateOnly: true })
                "
                @prev="onLitePrev(message)"
                @next="onLiteNext(message)"
                @accept-current="onLiteAcceptCurrent(message)"
                @reject-current="onLiteRejectCurrent(message)"
                @accept-all="onLiteAcceptAll(message)"
                @reject-all="onLiteRejectAll(message)"
                @done-reviewing="onLiteDoneReviewing(message)"
                @revert-all="onLiteRevertAll(message)"
                @revert-all-including-newer="
                  onLiteRevertAllIncludingNewer(message)
                "
              />
            </div>

            <div
              class="text-xs text-gray-500 dark_text-gray-400 mt-2 flex gap-4 w-full"
            >
              <span>{{ formatTime(getMessageCreatedAt(message)) }}</span>

              <!-- 👍👎 rating (assistant messages only, when traceId exists) -->
              <div
                v-if="
                  message.role === 'assistant' && getTraceIdForMessage(message)
                "
                class="flex items-center gap-2"
                :title="`Trace: ${getTraceIdForMessage(message)}`"
              >
                <button
                  type="button"
                  class="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-gray-300 dark_border-gray-600 bg-white text-gray-700 dark_bg-gray-800 dark_text-gray-200 btn-hover_bg-gray-50 btn-hover_text-gray-800 dark_btn-hover_bg-gray-700 dark_btn-hover_text-gray-100 btn-active_bg-gray-100 btn-active_text-gray-900 dark_btn-active_bg-gray-600 dark_btn-active_text-white disabled_bg-gray-100 disabled_text-gray-400 dark_disabled_bg-gray-800 dark_disabled_text-gray-500 transition-colors disabled_cursor-not-allowed"
                  :class="
                    isThumbsUp(message)
                      ? 'bg-gray-100 text-gray-900 dark_bg-gray-700 dark_text-white btn-hover_bg-gray-200 btn-hover_text-gray-900 dark_btn-hover_bg-gray-600 dark_btn-hover_text-white btn-active_bg-gray-300 btn-active_text-black dark_btn-active_bg-gray-500 dark_btn-active_text-white disabled_bg-gray-100 disabled_text-gray-400 dark_disabled_bg-gray-800 dark_disabled_text-gray-500'
                      : ''
                  "
                  :disabled="isRatingSending(message)"
                  @click="rateMessage(message, 'up')"
                >
                  <Icon
                    :icon="
                      isThumbsUp(message) ? 'ph:thumbs-up-fill' : 'ph:thumbs-up'
                    "
                  />
                </button>

                <button
                  type="button"
                  class="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-gray-300 dark_border-gray-600 bg-white text-gray-700 dark_bg-gray-800 dark_text-gray-200 btn-hover_bg-gray-50 btn-hover_text-gray-800 dark_btn-hover_bg-gray-700 dark_btn-hover_text-gray-100 btn-active_bg-gray-100 btn-active_text-gray-900 dark_btn-active_bg-gray-600 dark_btn-active_text-white disabled_bg-gray-100 disabled_text-gray-400 dark_disabled_bg-gray-800 dark_disabled_text-gray-500 transition-colors disabled_cursor-not-allowed"
                  :class="
                    isThumbsDown(message)
                      ? 'bg-gray-100 text-gray-900 dark_bg-gray-700 dark_text-white btn-hover_bg-gray-200 btn-hover_text-gray-900 dark_btn-hover_bg-gray-600 dark_btn-hover_text-white btn-active_bg-gray-300 btn-active_text-black dark_btn-active_bg-gray-500 dark_btn-active_text-white disabled_bg-gray-100 disabled_text-gray-400 dark_disabled_bg-gray-800 dark_disabled_text-gray-500'
                      : ''
                  "
                  :disabled="isRatingSending(message)"
                  @click="rateMessage(message, 'down')"
                >
                  <Icon
                    :icon="
                      isThumbsDown(message)
                        ? 'ph:thumbs-down-fill'
                        : 'ph:thumbs-down'
                    "
                  />
                </button>

                <span
                  v-if="ratingErrorByMessageId[message.id]"
                  class="text-red-600 dark_text-red-300"
                >
                  {{ ratingErrorByMessageId[message.id] }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Typing / reasoning indicator -->
        <div v-if="isStreaming" class="flex w-full self-start px-2 py-1">
          <div class="w-full rounded-xl px-3 py-2">
            <div class="flex items-center gap-2">
              <Icon
                icon="ph:spinner-gap-bold"
                class="text-blue-500 dark_text-blue-400 text-lg animate-spin"
                aria-hidden="true"
              />
              <p
                class="font-semibold text-sm bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-violet-500 to-fuchsia-500"
              >
                Analysing user request
              </p>
            </div>
            <p
              class="mt-0.5 text-xs text-slate-500 dark_text-slate-400 leading-relaxed"
            >
              {{ thinkingDescription }}
            </p>
          </div>
        </div>
      </div>

      <!-- Input Form -->
      <form
        :disabled="!yaml"
        @submit.prevent="handleSubmit"
        class="flex flex-col gap-2.5 p-2 sm_p-2.5 min-h-28 sm_min-h-32 m-2 sm_m-4 rounded-md bg-white dark_bg-gray-800 border border-slate-300 dark_border-gray-600"
      >
        <!-- Current Screen Pill -->
        <div
          v-if="currentScreen && !isLiteMode"
          class="flex items-center gap-2 px-3 py-1.5 text-xs chat-info-box"
        >
          <span class="text-blue-600 dark_text-blue-300 font-medium"
            >Current screen:</span
          >
          <span
            class="text-gray-800 dark_text-gray-100 bg-white dark_bg-gray-800 px-1.5 py-0.5 rounded border border-blue-200 dark_border-blue-600"
            >{{ currentScreenLabel }}</span
          >
        </div>

        <div
          v-if="creditMessage"
          class="flex items-center gap-2 px-3 py-1.5 rounded-md border text-xs font-medium"
          :class="
            canSendWithCredits === false
              ? 'bg-red-50 dark_bg-red-900/30 text-red-700 dark_text-red-200 border-red-200 dark_border-red-700'
              : 'bg-amber-50 dark_bg-amber-900/30 text-amber-800 dark_text-amber-200 border-amber-200 dark_border-amber-700'
          "
          role="status"
        >
          <Icon
            :icon="
              canSendWithCredits === false
                ? 'ph:warning-circle-fill'
                : 'ph:coins-fill'
            "
            class="text-base shrink-0"
            aria-hidden="true"
          />
          <span>{{ creditMessage }}</span>
        </div>

        <div
          class="flex gap-2.5 items-end min-h-12"
          :class="{ invisible: !yaml }"
        >
          <span
            class="shrink-0 self-start mt-2.5 px-2 inline-block w-8 dark:opacity-90 text-slate-400 dark_text-slate-500o"
            v-html="logoSimpleVwAi"
            aria-hidden="true"
          ></span>
          <textarea
            ref="textareaRef"
            v-model="input"
            :placeholder="placeholderMessage"
            :disabled="isInputLocked"
            @keydown.enter.exact.prevent="handleSubmit"
            rows="1"
            class="flex-1 min-w-0 min-h-28 max-h-72 [field-sizing:content] py-2.5 px-0 bg-transparent font-medium text-gray-900 dark_text-gray-100 placeholder-slate-400 dark_placeholder-slate-500 outline-none border-none resize-none overflow-y-auto overflow-x-hidden disabled:opacity-60"
            autocomplete="off"
            data-form-type="other"
            data-lpignore="true"
          ></textarea>
          <button
            :type="isStreaming ? 'button' : 'submit'"
            :disabled="isStreaming ? false : isInputLocked || !input.trim()"
            class="shrink-0 flex items-center justify-center w-9 h-9 rounded-full cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            :class="
              isStreaming
                ? 'bg-red-100 dark_bg-red-900/30 text-red-600 dark_text-red-300 hover:enabled:bg-red-200 dark_hover:enabled:bg-red-900/50'
                : 'bg-slate-200 dark_bg-gray-600 text-slate-600 dark_text-gray-300 hover:enabled:bg-slate-300 dark_hover:enabled:bg-gray-500'
            "
            :aria-label="isStreaming ? 'Stop' : 'Send'"
            @click="isStreaming && chat.stop()"
          >
            <Icon
              :icon="isStreaming ? 'ph:stop-fill' : 'ph:arrow-up'"
              class="w-4 h-4"
            />
          </button>
        </div>

        <!-- Error display + retry -->
        <div
          v-if="chat.status === 'error' && chat.error"
          class="mt-2 px-3 py-2 bg-red-100 dark_bg-red-900/30 text-red-800 dark_text-red-200 rounded text-xs flex items-center justify-between gap-3"
        >
          <span> Error: {{ chat.error.message }} </span>

          <button
            type="button"
            class="px-3 py-1 rounded-full text-xs font-semibold bg-red-200 dark_bg-red-800 hover:bg-red-300 dark_hover:bg-red-700 transition"
            :disabled="isInputLocked"
            @click="handleRegenerate"
          >
            Retry
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Chat } from '@ai-sdk/vue';
import { DefaultChatTransport, type UIMessage } from 'ai';
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
  inject,
  type MaybeRef,
  type Ref,
  toValue
} from 'vue';
import renderMarkdown from '../../lib/renderMarkdown';
import { load as yamlLoad } from 'js-yaml';
import { Icon } from '@iconify/vue';
import PatchPreview from './PatchPreview.vue';
import TextEditPreview from './TextEditPreview.vue';
import LitePatchReviewBar from './LitePatchReviewBar.vue';
import type { MessageDialogState } from '../lib/useMessageDialog';
import { EDITAGENT_API_BASE } from '../lib/editAgentApi';
import callClientPortal from '../../lib/callClientPortal';
import { getEncodedToken } from '../../lib/getJWT';
import {
  getMessageText,
  parseMessageContent,
  trimPartialTag
} from '../lib/messageUtils';
import useLitePatchReview from '../lib/useLitePatchReview';
import useLiteCheckpoints from '../lib/useLiteCheckpoints';
import {
  activeScreenIds,
  registrations,
  composePreviewForScreen
} from '../lib/useActivePreview';
import logoRoundVwAi from '../icons/logo-round-VW-ai.svg?raw';
import logoSimpleVwAi from '../icons/logo-VW-ai.svg?raw';
import type { EditAgentCreditsStatus } from '../lib/useEditAgentCredits';

const messagesContainer = ref<HTMLElement>();
const textareaRef = ref<HTMLTextAreaElement>();

type PatchState = 'accepted' | 'rejected' | 'pending';

const props = defineProps<{
  courseId: number;
  sessionId?: string;
  firmCode?: string;
  yaml?: string;
  currentScreen?: MaybeRef<Record<string, any>>;
  isLiteMode?: boolean;
  creditMessage?: string;
  canSendWithCredits?: boolean;
  refreshCredits?: () => Promise<EditAgentCreditsStatus | null | undefined>;
  consumeCredit?: () => Promise<EditAgentCreditsStatus | null | undefined>;
  /** Previously persisted messages used to seed the chat */
  initialMessages?: any[];
  /** Map of patch states keyed by `${messageId}_${editIndex}` */
  patchStates?: Record<string, PatchState>;
}>();

const yaml = computed(() => props.yaml);
const currentScreen = computed(() => toValue(props.currentScreen));
const patchStates = computed(() => props.patchStates);
const isLiteMode = computed(() => !!props.isLiteMode);

// Lite-mode pre-edit screen snapshots. Persisted (initialMessages) snapshots win;
// live snapshots fill the current-session gap. captureCheckpoints is called only
// from the isLiteMode paths below, so this is a no-op for full authoring (the map
// then equals the legacy initialMessages-only checkpoints).
const { captureCheckpoints, checkpointsByMessageId, liveCheckpoints } =
  useLiteCheckpoints({
    yaml,
    initialMessages: () => props.initialMessages
  });

watch(
  () => props.currentScreen,
  v => {
    console.log('AuthorEditAgent props.currentScreen', v);
  },
  { immediate: true }
);

function stripTags(content: string): string {
  return content
    .replace(/<COURSE>[\s\S]*?<\/COURSE>/g, '')
    .replace(/<SCREEN>[\s\S]*?<\/SCREEN>/g, '')
    .trim();
}

/**
 * Back-compat: if persisted messages are still `{ content: string }`,
 * convert them into v5 `{ parts: [{ type:'text', text }] }`.
 */
function normalizeMessages(messages: any[] = []): UIMessage[] {
  if (!Array.isArray(messages)) return [];

  return messages.map((m: any) => {
    // Already v5-correct
    if (Array.isArray(m?.parts) && m.parts.length > 0) {
      return m as UIMessage;
    }

    // v4-style or broken persistence
    const text =
      typeof m?.content === 'string'
        ? m.content
        : typeof m?.text === 'string'
          ? m.text
          : '';

    return {
      id: m?.id ?? crypto.randomUUID(),
      role: m?.role ?? 'assistant',
      metadata: m?.metadata ?? {},
      parts: text ? [{ type: 'text', text }] : []
    };
  });
}

// Merge this session's clean pre-edit snapshots into the persisted messages, so
// the saved checkpoints (used on reload + revert) match the live ones. Empty for
// full authoring, so it's a pass-through there. Existing metadata wins.
function stampLiveCheckpoints(messages: UIMessage[]): UIMessage[] {
  const live = liveCheckpoints.value;
  if (!Object.keys(live).length) return messages;
  return messages.map(m => {
    const cps = live[m.id];
    if (!cps) return m;
    const existing = (m.metadata as any)?.checkpoints ?? {};
    return {
      ...m,
      metadata: { ...(m.metadata ?? {}), checkpoints: { ...cps, ...existing } }
    } as UIMessage;
  });
}

// function buildTaggedMessage(text: string, isFirstMessage: boolean): string {
//   let tags = '';

//   if (isFirstMessage && props.yaml) {
//     tags += `<COURSE>\n${props.yaml}\n</COURSE>\n`;
//   }

//   const screenObj = unref(props.currentScreen);

//   if (screenObj) {
//     const screenContext = JSON.stringify(removeScreenIndex(screenObj), null, 2);
//     tags += `<SCREEN>\n${screenContext}\n</SCREEN>\n`;
//   }

//   return tags + text;
// }

const EDITAGENT_API_URL = `${EDITAGENT_API_BASE}/agent/run`;

const input = ref('');
const streamedStageDescription = ref('');

const DEFAULT_THINKING_DESCRIPTION =
  'Reviewing your request and gathering relevant context.';

/** Suggestion items for empty chat state */
type SuggestionAction = 'prompt' | 'openDocumentsDialog';

type SuggestionItem = {
  title: string;
  description: string;
  icon: string;
  prompt: string;
  action: SuggestionAction;
};

const suggestionItems: SuggestionItem[] = [
  {
    title: 'Explore what I can help with',
    description: 'Learn how I can support your editing and review process',
    icon: 'ph:wechat-logo-fill',
    prompt:
      'How can you help me quickly tailor and personalise this course to my audience?',
    action: 'prompt'
  },
  {
    title: 'Integrate a policy or document',
    description: 'Pull in or align text from your uploaded documents',
    icon: 'ph:file-text-fill',
    prompt:
      'Can you show me a list of all of the policies and documents I have uploaded that are relevant to this course?',
    action: 'openDocumentsDialog'
  },
  {
    title: 'Tailor course content',
    description:
      'Adjust tone, terminology and length to your preferences without losing key meaning',
    icon: 'ph:sliders-horizontal-fill',
    prompt: 'Apply a more professional tone to this course.',
    action: 'prompt'
  },
  {
    title: 'Add relevant contact details',
    description: 'Ensure your learners know who to turn to in any event',
    icon: 'ph:user-fill',
    prompt: 'Add the following contact details for this course.',
    action: 'prompt'
  }
];

function applySuggestion(suggestion: SuggestionItem) {
  if (suggestion.action === 'openDocumentsDialog') {
    emit('open-documents-dialog');
    return;
  }

  input.value = suggestion.prompt;
}

function resetThinkingDescription() {
  streamedStageDescription.value = '';
}

type RequestMeta = {
  courseId: string;
  sessionId: string;
  screenId?: string;
  turnNumber: number;
};

const currentRunTraceId = ref<string | null>(null);

function extractTraceIdFromDataPart(part: any): string | null {
  const t1 = part?.data?.traceId;
  if (typeof t1 === 'string' && t1.length) return t1;
  const t2 = part?.traceId;
  if (typeof t2 === 'string' && t2.length) return t2;
  return null;
}

function getStringPath(obj: any, path: string): string | null {
  const value = path
    .split('.')
    .reduce((acc: any, key: string) => acc?.[key], obj);
  return typeof value === 'string' && value.trim().length ? value.trim() : null;
}

function extractStageDescriptionFromDataPart(part: any): string | null {
  if (!part || typeof part !== 'object') return null;

  const candidates = [
    'description',
    'message',
    'statusMessage',
    'stageMessage',
    'status.message',
    'stage.description',
    'event.description',
    'event.message',
    'data.description',
    'data.message',
    'data.statusMessage',
    'data.stageMessage',
    'data.stage.description',
    'data.status.message',
    'data.event.description',
    'data.event.message'
  ];

  for (const path of candidates) {
    const text = getStringPath(part, path);
    if (text) return text;
  }

  return null;
}

function getTraceIdForMessage(message: UIMessage): string | null {
  const meta = (message as any)?.metadata;
  const traceId = meta?.traceId;
  return typeof traceId === 'string' && traceId.length ? traceId : null;
}

// Rating state
type Thumbs = 'up' | 'down';
const thumbsByMessageId = ref<Record<string, Thumbs | undefined>>({});
const ratingStatusByMessageId = ref<
  Record<string, 'idle' | 'sending' | 'sent' | 'error'>
>({});
const ratingErrorByMessageId = ref<Record<string, string>>({});

function isRatingSending(message: UIMessage) {
  return ratingStatusByMessageId.value[message.id] === 'sending';
}
function isThumbsUp(message: UIMessage) {
  return thumbsByMessageId.value[message.id] === 'up';
}
function isThumbsDown(message: UIMessage) {
  return thumbsByMessageId.value[message.id] === 'down';
}

async function rateMessage(message: UIMessage, thumbs: Thumbs) {
  const traceId = getTraceIdForMessage(message);
  if (!traceId) return;
  thumbsByMessageId.value[message.id] = thumbs;
  ratingStatusByMessageId.value[message.id] = 'sending';
  ratingErrorByMessageId.value[message.id] = '';
  const rating = thumbs === 'up' ? 5 : 1;
  try {
    await callClientPortal(
      '/rate',
      {
        method: 'POST',
        body: JSON.stringify({ traceId, rating })
      },
      { base: EDITAGENT_API_BASE }
    );
    ratingStatusByMessageId.value[message.id] = 'sent';
  } catch (e: any) {
    ratingStatusByMessageId.value[message.id] = 'error';
    ratingErrorByMessageId.value[message.id] =
      e?.message || 'Failed to send rating';
  }
}

const chat = new Chat<UIMessage>({
  messages: normalizeMessages(props.initialMessages),

  onError: (error: Error) => {
    console.error('Chat error:', error);
  },

  onData: (dataPart: any) => {
    const traceId = extractTraceIdFromDataPart(dataPart);
    if (traceId) {
      currentRunTraceId.value = traceId;
    }
    const stageDescription = extractStageDescriptionFromDataPart(dataPart);
    if (stageDescription) {
      streamedStageDescription.value = stageDescription;
    }
  },

  onFinish: async ({ message, messages }) => {
    resetThinkingDescription();

    // Capture synchronously, before any await yields to the ready watch (which
    // applies the preview overlay), so the snapshot stays a clean pre-edit base.
    if (props.isLiteMode && message?.role === 'assistant') {
      captureCheckpoints(message as UIMessage);
    }

    if (message?.role === 'assistant' && currentRunTraceId.value) {
      const traceId = currentRunTraceId.value;
      currentRunTraceId.value = null;

      // Find the assistant message in the messages array
      const target = messages.find(m => m.id === message.id);
      if (target) target.metadata = { ...(target.metadata ?? {}), traceId };
    }

    if (props.isLiteMode) {
      await props.consumeCredit?.();
    }

    console.log('Message finished:', message);
    emit('messages-updated', stampLiveCheckpoints(normalizeMessages(messages)));
  },

  transport: new DefaultChatTransport({
    api: EDITAGENT_API_URL,

    headers: () => ({
      Authorization: `Bearer: ${getEncodedToken()}`
    }),

    prepareSendMessagesRequest({
      messages,
      id,
      data
    }: {
      messages: UIMessage[];
      id: string;
      data?: RequestMeta;
    }) {
      const courseId = props.courseId.toString();
      const sessionId = props.sessionId ?? id;
      const screenId = toValue(props.currentScreen)?.id;

      const userTurnCount = messages.filter(m => m.role === 'user').length;
      const meta = data ?? {
        courseId,
        sessionId,
        screenId,
        turnNumber: userTurnCount
      };

      const lastUser = [...messages].reverse().find(m => m.role === 'user');
      const userMsg = lastUser ? getMessageText(lastUser) : '';

      return {
        body: {
          userTurnId: `turn-${meta.turnNumber ?? 1}`,
          userMsg,
          courseId: meta.courseId,
          sessionId: meta.sessionId,
          screenId: meta.screenId || undefined,
          firmCode: props.firmCode || undefined,
          compiledYaml: props.yaml || undefined
        }
      };
    }
  })
});

// Keep lite review navigation and patch grouping logic outside the SFC.
const {
  getLiteReviewForMessage,
  getLiteReviewStatusForMessage,
  getLiteNavigatorIndex,
  getCurrentLiteSelection,
  getCurrentLiteSelectionDecision,
  getShowPreviousVersion,
  setShowPreviousVersion,
  emitLiteSelection,
  onLitePrev,
  onLiteNext,
  onLiteAcceptCurrent,
  onLiteRejectCurrent,
  onLiteAcceptAll,
  onLiteRejectAll,
  onLiteDoneReviewing,
  clearActiveLitePreview
} = useLitePatchReview({
  messages: computed(() => chat.messages),
  yaml,
  patchStates,
  messageCheckpoints: checkpointsByMessageId,
  onPreviewPatch: (screenYaml: string) => previewPatch?.(screenYaml),
  onClearPreviewPatch: (screenId: string, keepDiff?: boolean) =>
    clearPreviewPatch?.(screenId, keepDiff),
  onViewPatch: (pageId: string, screenId: string) =>
    onViewPatch(pageId, screenId),
  onAcceptPatch: (messageId: string, editIndex: number, screenYaml: string) =>
    onAcceptPatch(messageId, editIndex, screenYaml),
  onRejectPatch: (messageId: string, editIndex: number) =>
    onRejectPatch(messageId, editIndex),
  onRevertScreen: (screenId: string, messageId: string, editIndex: number) => {
    clearPreviewPatch?.(screenId);
    revertPatch?.(screenId, messageId, editIndex);
  }
});

watch(
  () => chat.status,
  (status, prev) => {
    if (prev !== 'streaming' || status !== 'ready') return;
    const last = chat.messages[chat.messages.length - 1];
    if (!last || last.role !== 'assistant') return;
    const chunks = parseMessageContent(getMessageText(last));
    const edits: Record<string, number> = {};
    const textEdits: Record<string, number> = {};
    for (const c of chunks) {
      if (!c.complete || (c.type !== 'edit' && c.type !== 'text-edit'))
        continue;
      const m = c.content.match(/"(?:id|screenId)"\s*:\s*"([^"]+)"/);
      const id = m?.[1] ?? '?';
      const bucket = c.type === 'edit' ? edits : textEdits;
      bucket[id] = (bucket[id] ?? 0) + 1;
    }
    console.log('[EditAgent]', JSON.stringify({ edits, textEdits }));
  }
);

// v5 statuses: submitted | streaming | ready | error
const isStreaming = computed(
  () => chat.status === 'submitted' || chat.status === 'streaming'
);

const isMessageStreaming = (message: UIMessage) => {
  if (!isStreaming.value) return false;
  const last = chat.messages[chat.messages.length - 1];
  return last != null && last.id === message.id;
};

const latestStreamingAssistantText = computed(() => {
  if (!isStreaming.value) return '';

  for (let i = chat.messages.length - 1; i >= 0; i--) {
    const message = chat.messages[i];
    if (message.role === 'assistant') {
      return getMessageText(message).trim();
    }
  }

  return '';
});

const thinkingDescription = computed(() => {
  if (streamedStageDescription.value) {
    return streamedStageDescription.value;
  }

  if (chat.status === 'submitted') {
    return DEFAULT_THINKING_DESCRIPTION;
  }

  const text = latestStreamingAssistantText.value;
  if (!text) {
    return 'Working through the best response and validating any edits.';
  }
  if (text.includes('<edit>')) {
    return 'Preparing proposed screen changes for your review.';
  }
  return 'Drafting response and checking course context.';
});

const hasPendingPatchDecision = computed(() => {
  const states = patchStates.value ?? {};

  for (const message of chat.messages) {
    if (message.role !== 'assistant') continue;
    const chunks = parseMessageContent(getMessageText(message));

    for (const chunk of chunks) {
      // Count both edit and text-edit chunks — full mode reviews both as cards.
      if (
        (chunk.type !== 'edit' && chunk.type !== 'text-edit') ||
        !chunk.complete
      )
        continue;
      const state = states[`${message.id}_${chunk.index}`];
      if (state !== 'accepted' && state !== 'rejected') {
        return true;
      }
    }
  }

  return false;
});

function getLatestLiteMessageWithChanges(): UIMessage | null {
  for (let i = chat.messages.length - 1; i >= 0; i--) {
    const message = chat.messages[i];
    if (message.role !== 'assistant') continue;
    const review = getLiteReviewForMessage(message);
    if (review.allGroups.length > 0) return message;
  }
  return null;
}

function hasNewerNonRejectedEdits(currentMsgIndex: number): boolean {
  const states = patchStates.value ?? {};
  for (const message of (chat.messages as UIMessage[]).slice(
    currentMsgIndex + 1
  )) {
    if (message.role !== 'assistant') continue;
    const hasNonRejected = parseMessageContent(getMessageText(message)).some(
      chunk => {
        if (
          (chunk.type !== 'edit' && chunk.type !== 'text-edit') ||
          !chunk.complete
        ) {
          return false;
        }
        return (
          (states[`${message.id}_${chunk.index}`] ?? 'pending') !== 'rejected'
        );
      }
    );
    if (hasNonRejected) return true;
  }
  return false;
}

const isInputLocked = computed(
  () => isStreaming.value || props.canSendWithCredits === false
);

const pendingLiteMessages = computed<UIMessage[]>(() =>
  isLiteMode.value
    ? chat.messages.filter(
        m =>
          m.role === 'assistant' &&
          getLiteReviewForMessage(m).pendingEdits.length > 0
      )
    : []
);

// Shared host dialog (one instance → pending-review and editing-paused dialogs
// can never co-exist).
const openMessageDialog = inject<(cfg: Partial<MessageDialogState>) => void>(
  'openMessageDialog',
  () => {}
);
const closeMessageDialog = inject<() => void>('closeMessageDialog', () => {});

// Lock the preview's inline editing while the agent streams OR while edits still
// await an accept/reject decision — either way, user edits would go stale. Key the
// watch on the 3-state reason (not the boolean) so it also fires on the
// streaming→review transition, keeping the "editing paused" dialog text accurate.
const inlineEditLocked = inject<Ref<boolean> | null>('inlineEditLocked', null);
const inlineEditLockReason = inject<Ref<'streaming' | 'review' | null> | null>(
  'inlineEditLockReason',
  null
);
const hasPendingReview = computed(() =>
  isLiteMode.value
    ? pendingLiteMessages.value.length > 0
    : hasPendingPatchDecision.value
);
const inlineEditLockState = computed<'streaming' | 'review' | null>(() =>
  isStreaming.value ? 'streaming' : hasPendingReview.value ? 'review' : null
);
watch(
  inlineEditLockState,
  reason => {
    if (inlineEditLocked) inlineEditLocked.value = Boolean(reason);
    if (inlineEditLockReason) inlineEditLockReason.value = reason;
  },
  { immediate: true }
);

async function refreshCreditsBeforeRun() {
  if (!props.isLiteMode) return true;
  const nextStatus = await props.refreshCredits?.();
  return nextStatus?.canSend !== false;
}

async function handleSubmit() {
  if (isStreaming.value || !input.value.trim()) return;
  if (pendingLiteMessages.value.length > 0) {
    openPendingReviewDialog();
    return;
  }
  await sendCurrentInput();
}

async function sendCurrentInput() {
  const canProceed = await refreshCreditsBeforeRun();
  if (!canProceed || isInputLocked.value || !input.value.trim()) return;
  resetThinkingDescription();
  // Fire-and-forget commit of any accepted-but-uncommitted edits. The request
  // now carries the full compiled YAML, so the gatherer reads the course from
  // the payload rather than the committed draft — no need to block the send on
  // the commit landing. The commit still runs for persistence/git history.
  void flushPendingCommit?.();
  chat.sendMessage({
    text: input.value,
    metadata: { createdAt: new Date().toISOString() }
  });
  input.value = '';
}

async function resolvePendingReview(action: (m: UIMessage) => void) {
  closeMessageDialog();
  pendingLiteMessages.value.forEach(action);
  await nextTick();
  await sendCurrentInput();
}

const pendingReviewActions = computed(() => [
  { label: 'Cancel', primary: false, onClick: () => closeMessageDialog() },
  {
    label: 'Reject all changes',
    primary: false,
    onClick: () => resolvePendingReview(onLiteRejectAll)
  },
  {
    label: 'Accept all changes',
    primary: true,
    onClick: () => resolvePendingReview(onLiteAcceptAll)
  }
]);

function openPendingReviewDialog() {
  openMessageDialog({
    id: 'pending-review',
    title: 'You have changes to review',
    message:
      'Please accept or reject the proposed changes before sending a new message.',
    actions: pendingReviewActions.value
  });
}

async function handleRegenerate() {
  if (isStreaming.value) return;
  const canProceed = await refreshCreditsBeforeRun();
  if (!canProceed || isInputLocked.value) return;
  resetThinkingDescription();
  void flushPendingCommit?.();
  chat.regenerate();
}

watch(() => chat.messages, scrollMessagesToBottom, {
  deep: true,
  flush: 'post'
});

async function scrollMessagesToBottom() {
  await nextTick();
  if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
  }
}

onMounted(() => {
  void scrollMessagesToBottom();
  if (isLiteMode.value && !isStreaming.value) {
    const latest = getLatestLiteMessageWithChanges();
    if (latest) emitLiteSelection(latest);
  }
});

watch(
  () => chat.status,
  status => {
    if (status === 'ready' || status === 'error') {
      resetThinkingDescription();
      nextTick().then(() => {
        textareaRef.value?.focus();
        if (isLiteMode.value && !isStreaming.value) {
          const latest = getLatestLiteMessageWithChanges();
          if (latest) {
            // Snapshot before emitLiteSelection applies the preview overlay, so
            // the checkpoint is a clean pre-edit base for oldText matching.
            captureCheckpoints(latest);
            emitLiteSelection(latest);
          }
        }
      });
    }
  }
);

const formatTime = (date?: Date | string) => {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const placeholderMessage = computed(() => {
  if (hasPendingPatchDecision.value) {
    return 'Accept or reject the current change to continue...';
  }
  return chat.messages.length === 0
    ? `What do you want to change?`
    : 'Type your message...';
});

function getMessageCreatedAt(message: UIMessage): string | Date | undefined {
  const metadataCreatedAt = (message as any)?.metadata?.createdAt;
  if (
    typeof metadataCreatedAt === 'string' ||
    metadataCreatedAt instanceof Date
  ) {
    return metadataCreatedAt;
  }
  return undefined;
}

const currentScreenLabel = computed(() => {
  const s = props.currentScreen as any;
  if (!s) return '';
  return s.heading || s.subheading || s.title || s.id || '';
});

// Expose methods for parent components if needed
defineExpose({
  reload: () => chat.regenerate(),
  stop: () => chat.stop(),
  messages: chat.messages,
  attachDocument: async (title: string) => {
    const prefix = `Using the document '${title}': `;
    input.value = input.value.trim() ? `${prefix}\n${input.value}` : prefix;
    await nextTick();
    const ta = textareaRef.value;
    if (ta) {
      ta.focus();
      ta.setSelectionRange(prefix.length, prefix.length);
    }
  }
});

// Inject accept/preview/revert handlers provided by AuthorEditor
const acceptPatch =
  inject<(yaml: string, messageId: string, editIndex: number) => void>(
    'acceptPatch'
  );
const previewPatch = inject<(yaml: string) => void>('previewPatch');
const clearPreviewPatch =
  inject<(screenId: string, keepDiff?: boolean) => void>('clearPreviewPatch');
const revertPatch =
  inject<(screenId: string, messageId: string, editIndex: number) => void>(
    'revertPatch'
  );
// Commits any pending accepted edits and waits for the commit to land, so the
// gatherer reads the committed course instead of racing the draft save.
const flushPendingCommit = inject<() => Promise<void>>('flushPendingCommit');

// Handlers for PatchPreview events
function onAcceptPatch(
  messageId: string,
  editIndex: number,
  screenYaml: string
) {
  console.log('Accept clicked for patch', { messageId, editIndex });

  try {
    const parsed = yamlLoad(screenYaml);
    if (!parsed || typeof parsed !== 'object' || !(parsed as any).id) {
      throw new Error('Patch YAML missing id');
    }

    const screenId = (parsed as any).id;

    acceptPatch?.(screenYaml, messageId, editIndex);
    console.log('✅ Patch sent to Monaco');

    // TODO: DELETE this after testing. This was commented out because it was resetting the content to the old version after the patch had
    // been accepted. Causing the preview to revert to the old version even though the patch had been accepted.
    // setTimeout(() => {
    //   clearPreviewPatch?.(screenId);
    //   console.log('✅ Cleared preview after accept');
    // }, 300);

    emit('patch-state', messageId, editIndex, 'accepted');
  } catch (err: any) {
    console.error('Failed to apply patch:', err);
    window.alert((err as any).message || 'Failed to apply patch');
  }
}

function onRejectPatch(messageId: string, editIndex: number) {
  console.log('Reject clicked for patch', { messageId, editIndex });
  emit('patch-state', messageId, editIndex, 'rejected');
}

function onRevertPatch(
  messageId: string,
  editIndex: number,
  screenId: string | null
) {
  console.log('Revert clicked for patch', { messageId, editIndex, screenId });

  try {
    if (screenId) {
      clearPreviewPatch?.(screenId);
      console.log('✅ Cleared preview before revert');
      revertPatch?.(screenId, messageId, editIndex);
      console.log('✅ Patch reverted from Monaco');
      emit('patch-state', messageId, editIndex, 'pending');
    } else {
      console.warn('No screenId provided for revert');
    }
  } catch (err) {
    console.error('Failed to revert patch from Monaco:', err);
  }
}

function onUnrejectPatch(messageId: string, editIndex: number) {
  console.log('Un-reject clicked for patch', { messageId, editIndex });
  emit('patch-state', messageId, editIndex, 'pending');
}

function getScreenIdFromChunk(chunk: {
  type: string;
  content: string;
}): string | null {
  try {
    const parsed = yamlLoad(chunk.content) as any;
    const parsedId = chunk.type === 'edit' ? parsed?.id : parsed?.screenId;
    if (typeof parsedId === 'string' && parsedId.length) return parsedId;
  } catch {
    // no-op; try regex fallback below
  }

  const match = chunk.content.match(/"(?:id|screenId)"\s*:\s*"([^"]+)"/);
  return match?.[1] ?? null;
}

function onLiteRevertAll(message: UIMessage) {
  const chunks = parseMessageContent(getMessageText(message));
  const states = patchStates.value ?? {};

  for (const chunk of chunks) {
    if (
      (chunk.type !== 'edit' && chunk.type !== 'text-edit') ||
      !chunk.complete
    )
      continue;

    const state = states[`${message.id}_${chunk.index}`] ?? 'pending';
    if (state === 'accepted') {
      const screenId = getScreenIdFromChunk(chunk);
      onRevertPatch(message.id, chunk.index, screenId);
    } else if (state === 'rejected') {
      onUnrejectPatch(message.id, chunk.index);
    }
  }

  // Re-apply previews once patchStates have propagated
  nextTick().then(() => emitLiteSelection(message));
}

/** Revert this message's edits and reject all edits in newer messages. */
function onLiteRevertAllIncludingNewer(message: UIMessage) {
  const idx = chat.messages.findIndex((m: UIMessage) => m.id === message.id);
  if (idx < 0) return;

  const states = patchStates.value ?? {};

  // Reject all edits in messages newer than the one being reverted.
  // For accepted edits, first revert the YAML change then mark rejected.
  for (let i = idx + 1; i < (chat.messages as UIMessage[]).length; i++) {
    const newerMessage = (chat.messages as UIMessage[])[i];
    if (newerMessage.role !== 'assistant') continue;

    const chunks = parseMessageContent(getMessageText(newerMessage));
    for (const chunk of chunks) {
      if (
        (chunk.type !== 'edit' && chunk.type !== 'text-edit') ||
        !chunk.complete
      )
        continue;

      const state = states[`${newerMessage.id}_${chunk.index}`] ?? 'pending';
      if (state === 'accepted') {
        try {
          const screenId = getScreenIdFromChunk(chunk);
          if (screenId) {
            clearPreviewPatch?.(screenId);
            revertPatch?.(screenId, newerMessage.id, chunk.index);
            emit('patch-state', newerMessage.id, chunk.index, 'rejected');
          }
        } catch (err) {
          console.error(
            'Failed to revert accepted patch during revert-all:',
            err
          );
        }
      } else if (state === 'pending') {
        emit('patch-state', newerMessage.id, chunk.index, 'rejected');
      }
      // 'rejected' → already rejected, no-op
    }
  }

  // Revert the current message's own edits
  onLiteRevertAll(message);
}

function onViewPatch(pageId: string, screenId: string) {
  console.log('View clicked for patch', { pageId, screenId });
  emit('view-patch', pageId, screenId);
}

// Sync active preview registrations to Monaco. Multiple chunks may register
// against the same screen; compose their edits in order for a stacked preview.
const lastPreviewedScreens = new Set<string>();
watch(
  [activeScreenIds, registrations, () => yaml.value],
  () => {
    const nowIds = new Set(activeScreenIds.value);

    for (const id of lastPreviewedScreens) {
      if (!nowIds.has(id)) {
        try {
          clearPreviewPatch?.(id);
        } catch (e) {
          console.error('[preview] clear failed:', e);
        }
      }
    }

    for (const id of nowIds) {
      const composed = composePreviewForScreen(id, yaml.value || '');
      if (composed) {
        try {
          previewPatch?.(composed);
        } catch (e) {
          console.error('[preview] push failed:', e);
        }
      }
    }

    lastPreviewedScreens.clear();
    for (const id of nowIds) lastPreviewedScreens.add(id);
  },
  { deep: true, immediate: true }
);

const emit = defineEmits<{
  /** Emitted when author clicks View on a patch - contains pageId and screenId */
  (e: 'view-patch', pageId: string, screenId: string): void;
  /** Emitted when user selects the documents suggestion from empty state */
  (e: 'open-documents-dialog'): void;
  /** Emitted when patch accepted/rejected – used for persistence */
  (
    e: 'patch-state',
    messageId: string,
    editIndex: number,
    state: PatchState
  ): void;
  /** Emitted whenever messages array changes so wrapper can persist */
  (e: 'messages-updated', messages: any[]): void;
}>();

onBeforeUnmount(() => {
  clearActiveLitePreview();
  if (inlineEditLocked) inlineEditLocked.value = false;
  if (inlineEditLockReason) inlineEditLockReason.value = null;
});
</script>
<style scoped>
.chat-info-box {
  border-radius: 4px;
  border: 1px solid var(--Colors-indigo-100, #e0e7ff);
  background: rgba(245, 243, 255, 0.56);

  @apply dark_border-gray-600 dark_bg-gray-800/30;
}
</style>
